import React from 'react';
import { motion } from 'motion/react';
import { ShieldCheck, Leaf } from 'lucide-react';
import { LeafCluster } from '../components/WoodlandSVG';
import { fadeUp, pageTransition, viewportConfig } from '../components/AnimationVariants';

const sections = [
  {
    title: 'About This Policy',
    body: `Woodlands Orthodontics is committed to protecting your personal data and complying with current data protection legislation, including the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. This policy explains what personal information we collect, why we collect it, and how we use it.`,
  },
  {
    title: 'Personal Data We Collect',
    body: `We collect personal information to provide you with orthodontic care. This includes:
• Patient identification and contact details (name, address, date of birth, email, phone number)
• Medical and dental histories necessary for your treatment
• Family member information for emergency contacts
• Employment data for staff members`,
  },
  {
    title: 'Special Category Data',
    body: `In the course of providing clinical care, we process special category data, including:
• Health and dental records required to deliver your treatment
• Equality and diversity information where relevant
• Criminal record checks for employees as required by law`,
  },
  {
    title: 'How We Use Your Data',
    body: `Your personal data is used solely to provide and manage your orthodontic care. We minimise the data we keep and do not retain it for longer than necessary. We do not share your details with third parties without appropriate contracts or your explicit consent in place.

If you do not wish certain personal data to be disclosed or used in a particular way, please discuss this directly with your orthodontist.`,
  },
  {
    title: 'Data Retention',
    body: `We retain personal data for the following minimum periods:
• Patient records: 10 years from the last date of treatment
• Staff employment records: 6 years after employment ends
• Other personal data: 2 years after the last processing activity`,
  },
  {
    title: 'Your Rights',
    body: `You have the right to request access to the personal data we hold about you. Requests should be made in writing and will be responded to within 40 days. You also have the right to:
• Correct inaccurate or incomplete data
• Request deletion of your data (subject to legal obligations)
• Object to or restrict certain types of processing
• Lodge a complaint with the Information Commissioner's Office (ICO)`,
  },
  {
    title: 'Privacy Notice for Children',
    body: `For patients under the age of 18, Woodlands Orthodontics maintains records in strict confidence until the patient reaches the age of 25. We will only share personal information with other healthcare professionals where it is necessary to deliver appropriate care.

When a patient turns 18, their privacy rights transition to full adult standard protections.`,
  },
  {
    title: 'Cookies',
    body: `Our website uses session and persistent cookies (including Google Analytics cookies: __utma, __utmb, __utmc, __utmz) to improve your browsing experience and analyse how the site is used. Cookies do not personally identify you, but may be linked to personal information we hold. You may disable cookies in your browser settings, though this may affect site functionality.`,
  },
  {
    title: 'Contact Us',
    body: `If you have any questions about this privacy policy or how we handle your data, please contact us:

Woodlands Orthodontics
106 Woodlands, North Harrow, HA2 6EW
Phone: 020 8866 5556
Email: info@woodlandsorthodontics.co.uk`,
  },
];

export default function PrivacyPolicy() {
  return (
    <motion.div {...pageTransition} className="overflow-x-hidden pt-24">

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-forest-dark to-forest overflow-hidden">
        <motion.div className="absolute right-0 top-0 w-64 opacity-15 hidden md:block"
          animate={{ y: [0, -10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
          <LeafCluster />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-sage-light text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Leaf className="w-4 h-4" /> Legal
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-5"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            Privacy Policy
          </motion.h1>
          <motion.p className="text-cream/75 text-lg leading-relaxed"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            How we collect, use, and protect your personal information.
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-cream">
        <div className="container-max max-w-3xl">
          <motion.div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-cream-deeper"
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-cream-deeper">
              <div className="w-10 h-10 rounded-xl bg-sage-pale flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-forest" />
              </div>
              <p className="text-sm text-gray-500">Last updated: May 2025 · Woodlands Orthodontics</p>
            </div>

            <div className="space-y-10">
              {sections.map((s, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }} viewport={{ once: true }}>
                  <h2 className="font-serif text-forest text-xl font-semibold mb-3">{s.title}</h2>
                  <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{s.body}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
